<template>
    <div class="user-selector-container">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-bar">
          <el-input v-model="searchForm.keyword" placeholder="搜索OpenID、备注" clearable
            style="width: 280px; margin-right: 12px" @keyup.enter="handleSearch" @clear="handleClearSearch">
            <template #prefix>
              <el-icon>
                <Search />
              </el-icon>
            </template>
          </el-input>
  
          <el-select v-model="searchForm.tag_id" placeholder="按标签筛选" clearable style="width: 180px; margin-right: 12px"
            @change="handleSearch">
            <el-option v-for="tag in tags" :key="tag.tag_id" :label="`${tag.tag_name} (${tag.user_count})`"
              :value="tag.tag_id" />
          </el-select>
  
          <el-select v-model="searchForm.subscribe" placeholder="关注状态" clearable style="width: 120px; margin-right: 12px"
            @change="handleSearch">
            <el-option label="已关注" :value="1" />
            <el-option label="未关注" :value="0" />
          </el-select>
  
          <el-button type="primary" @click="handleSearch">
            <el-icon>
              <Search />
            </el-icon>
            搜索
          </el-button>
          <el-button @click="handleClearSearch">
            重置
          </el-button>
        </div>
      </div>
  
      <!-- 已选择用户提示 -->
      <div v-if="selectedUsers.length > 0" class="selected-info">
        <el-alert type="info" :closable="false" show-icon>
          <template #title>
            <span>已选择 <strong class="selected-count">{{ selectedUsers.length }}</strong> 个用户</span>
            <el-button type="text" size="small" @click="clearSelection" style="margin-left: 15px;">
              <el-icon>
                <Close />
              </el-icon>
              清空
            </el-button>
          </template>
        </el-alert>
      </div>
  
      <!-- 用户列表 -->
      <div class="user-list-section">
        <el-table :data="userList" stripe style="width: 100%; height: 400px" v-loading="loading"
          @selection-change="handleSelectionChange" ref="userTableRef">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="openid" label="OpenID" width="220">
            <template #default="{ row }">
              <div class="openid-cell">
                <el-tooltip :content="row.openid" placement="top">
                  <span class="openid-text">{{ truncateOpenId(row.openid) }}</span>
                </el-tooltip>
                <el-button type="text" size="small" @click="copyToClipboard(row.openid)">
                  <el-icon>
                    <CopyDocument />
                  </el-icon>
                </el-button>
              </div>
            </template>
          </el-table-column>
  
          <el-table-column prop="remark" label="备注" width="120">
            <template #default="{ row }">
              <span v-if="row.remark" class="remark-text">{{ row.remark }}</span>
              <el-tag v-else size="small" type="info">无备注</el-tag>
            </template>
          </el-table-column>
  
          <el-table-column prop="tagid_list" label="标签" width="150">
            <template #default="{ row }">
              <div class="tags-cell">
                <el-tag v-for="tagId in (row.tagid_list || []).slice(0, 2)" :key="tagId" size="small" class="user-tag">
                  {{ getTagName(tagId) }}
                </el-tag>
                <el-tag v-if="(row.tagid_list || []).length > 2" size="small" type="info">
                  +{{ (row.tagid_list || []).length - 2 }}
                </el-tag>
                <span v-if="!row.tagid_list || row.tagid_list.length === 0" class="no-tags">无标签</span>
              </div>
            </template>
          </el-table-column>
  
          <el-table-column prop="subscribe_time" label="关注时间" width="140">
            <template #default="{ row }">
              {{ formatSubscribeTime(row.subscribe_time) }}
            </template>
          </el-table-column>
  
          <el-table-column label="关注状态" width="80">
            <template #default="{ row }">
              <el-tag :type="row.subscribe === 1 ? 'success' : 'danger'" size="small">
                {{ row.subscribe === 1 ? '已关注' : '未关注' }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
  
        <!-- 分页 -->
        <div class="pagination-container" v-if="pagination.total > 0">
          <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
            :total="pagination.total" :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
            @size-change="handlePageChange" @current-change="handlePageChange" />
        </div>
  
        <!-- 空状态 -->
        <el-empty v-if="userList.length === 0 && !loading" description="暂无用户数据">
          <el-button type="primary" @click="fetchUsers">刷新列表</el-button>
        </el-empty>
      </div>
  
      <!-- 底部操作按钮 -->
      <div class="action-footer">
        <div class="selected-summary">
          <span>已选择: <strong>{{ selectedUsers.length }}</strong> 个用户</span>
        </div>
        <div class="action-buttons">
          <el-button @click="$emit('cancel')">取消</el-button>
          <el-button type="primary" @click="confirmSelection" :disabled="selectedUsers.length === 0">
            确认选择 ({{ selectedUsers.length }})
          </el-button>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import config from '@/config.js';
  import {
    Search, CopyDocument, Close
  } from '@element-plus/icons-vue'
  
  export default {
    name: 'WechatUserSelector',
    components: { Search, CopyDocument, Close },
    props: {
      accountId: {
        type: Number,
        required: true
      },
      storeId: {
        type: Number,
        required: true
      },
      preSelectedOpenids: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        loading: false,
        userList: [],
        selectedUsers: [],
        tags: [],
        searchForm: {
          keyword: '',
          tag_id: '',
          subscribe: 1 // 默认只显示已关注用户
        },
        pagination: {
          page: 1,
          pageSize: 20,
          total: 0
        },
        isSearching: false
      }
    },
    watch: {
      accountId: {
        immediate: true,
        handler(newVal) {
          if (newVal) {
            this.fetchUsers();
            this.fetchTags();
          }
        }
      }
    },
    mounted() {
      // 如果传入预选openids，需要预先选中
      this.$nextTick(() => {
        if (this.preSelectedOpenids.length > 0) {
          this.preSelectUsers();
        }
      });
    },
    methods: {
      // 获取用户列表
      async fetchUsers() {
        if (!this.accountId) return;
  
        this.loading = true;
        try {
          const requestData = {
            action: 'search_users',
            account_id: this.accountId,
            store_id: this.storeId,
            page: this.pagination.page,
            page_size: this.pagination.pageSize
          };
  
          // 添加搜索条件
          if (this.isSearching) {
            if (this.searchForm.keyword) {
              requestData.keyword = this.searchForm.keyword;
            }
            if (this.searchForm.tag_id) {
              requestData.tag_id = this.searchForm.tag_id;
            }
            if (this.searchForm.subscribe !== undefined) {
              requestData.subscribe = this.searchForm.subscribe;
            }
          } else {
            // 默认只显示已关注用户
            requestData.subscribe = 1;
          }
  
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/wechat_account.php`,
            requestData
          );
  
          if (data.success) {
            this.userList = data.data.user_list || [];
            this.pagination.total = data.pagination?.total || 0;
  
            // 数据加载后，重新选中之前选中的用户
            if (this.selectedUsers.length > 0) {
              this.$nextTick(() => {
                this.restoreSelection();
              });
            }
          } else {
            this.$message.error(data.error || '获取用户失败');
          }
        } catch (error) {
          this.$message.error('获取用户失败');
          console.error('获取用户错误:', error);
        } finally {
          this.loading = false;
        }
      },
  
      // 获取标签列表
      async fetchTags() {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/wechat_account.php`,
            {
              action: 'get_tags',
              account_id: this.accountId,
              store_id: this.storeId
            }
          );
  
          if (data.success) {
            this.tags = data.data.tags || [];
          }
        } catch (error) {
          console.error('获取标签错误:', error);
        }
      },
  
      // 处理搜索
      handleSearch() {
        this.pagination.page = 1;
        this.isSearching = true;
        this.fetchUsers();
      },
  
      // 清空搜索
      handleClearSearch() {
        this.searchForm = {
          keyword: '',
          tag_id: '',
          subscribe: 1
        };
        this.isSearching = false;
        this.pagination.page = 1;
        this.fetchUsers();
      },
  
      // 处理分页变化
      handlePageChange() {
        this.fetchUsers();
      },
  
      // 处理选择变化
      handleSelectionChange(selection) {
        this.selectedUsers = selection;
      },
  
      // 预选用户（根据传入的openids）
      preSelectUsers() {
        if (!this.userList.length) return;
  
        this.$nextTick(() => {
          const selectedRows = this.userList.filter(user =>
            this.preSelectedOpenids.includes(user.openid)
          );
          selectedRows.forEach(row => {
            this.$refs.userTableRef.toggleRowSelection(row, true);
          });
          this.selectedUsers = selectedRows;
        });
      },
  
      // 恢复选择状态（分页后保持选中）
      restoreSelection() {
        if (!this.$refs.userTableRef || !this.selectedUsers.length) return;
  
        this.userList.forEach(row => {
          const isSelected = this.selectedUsers.some(
            selected => selected.openid === row.openid
          );
          if (isSelected) {
            this.$refs.userTableRef.toggleRowSelection(row, true);
          }
        });
      },
  
      // 清空选择
      clearSelection() {
        if (this.$refs.userTableRef) {
          this.$refs.userTableRef.clearSelection();
        }
        this.selectedUsers = [];
      },
  
      // 确认选择
      confirmSelection() {
        if (this.selectedUsers.length === 0) {
          this.$message.warning('请先选择用户');
          return;
        }
  
        this.$emit('confirm', this.selectedUsers);
      },
  
      // 格式化关注时间
      formatSubscribeTime(timestamp) {
        if (!timestamp) return '-';
        const date = new Date(timestamp * 1000);
        return date.toLocaleDateString('zh-CN');
      },
  
      // 获取标签名称
      getTagName(tagId) {
        const tag = this.tags.find(t => t.tag_id == tagId);
        return tag ? tag.tag_name : tagId;
      },
  
      // 截断OpenID显示
      truncateOpenId(openid) {
        if (!openid) return '';
        if (openid.length <= 20) return openid;
        return openid.substring(0, 15) + '...' + openid.substring(openid.length - 5);
      },
  
      // 复制到剪贴板
      copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('已复制到剪贴板');
        }).catch(() => {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          this.$message.success('已复制到剪贴板');
        });
      }
    }
  }
  </script>
  
  <style scoped>
  .user-selector-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  
  .search-section {
    margin-bottom: 16px;
  }
  
  .search-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .selected-info {
    margin-bottom: 16px;
  }
  
  .selected-info .el-alert {
    padding: 8px 16px;
  }
  
  .selected-count {
    color: #409eff;
    font-size: 18px;
  }
  
  .user-list-section {
    flex: 1;
    overflow: hidden;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 10px;
    background: #fff;
  }
  
  .openid-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  .openid-text {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #666;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .remark-text {
    font-weight: 500;
    color: #333;
  }
  
  .tags-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .user-tag {
    background: #f0f9ff;
    color: #1890ff;
    border: 1px solid #91d5ff;
    margin: 2px;
  }
  
  .no-tags {
    color: #999;
    font-size: 12px;
  }
  
  .pagination-container {
    margin-top: 16px;
    display: flex;
    justify-content: center;
  }
  
  .action-footer {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .selected-summary {
    font-size: 14px;
    color: #606266;
  }
  
  .selected-summary strong {
    color: #409eff;
    font-size: 16px;
  }
  
  .action-buttons {
    display: flex;
    gap: 12px;
  }
  </style>